import { DeveloperDashboardLayout } from "@layouts/DeveloperDashboardLayout";
import React, { useState, ChangeEvent } from "react";
import image from "../../../assets/images/profile-pic.png";
import ProfileImageUpload from "./image-upload";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSessionStorage } from "../../../hooks";
import { updateProfile } from "@services/krentDeveloper";

const EditDeveloperProfile: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useSessionStorage("user", {});

  const { state }: { state: any } = location;
  const [profile, setProfile] = useState({
    profileImage:
      state?.profileInfo.photo !== "default.jpg"
        ? state?.profileInfo.photo
        : image,
    firstname: state?.profileInfo.firstname || "",
    lastname: state?.profileInfo.lastname || "",
    companyName: state?.profileInfo.companyName || "",
    username: state?.profileInfo.preferredName || "",
    companyBio: state?.profileInfo.companyBio || "",
    companyWebsite: state?.profileInfo.companyWebsite || "",
    phoneNumber: state?.profileInfo.phone || "",
    email: state?.profileInfo.email || "",
    companyAddress: state?.profileInfo.companyAddress || "",
  });

  const [isAccountInfoOpen, setIsAccountInfoOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    // Phone number validation
    if (name === "phoneNumber") {
      // Only allow numbers
      const numbersOnly = value.replace(/\D/g, "");
      // Limit to 13 digits
      const limitedValue = numbersOnly.slice(0, 13);

      setProfile((prevProfile) => ({ ...prevProfile, [name]: limitedValue }));

      // Validate phone number
      if (
        limitedValue.length > 0 &&
        (limitedValue.length < 10 || limitedValue.length > 13)
      ) {
        setPhoneError("Phone number must be between 10 and 13 digits");
      } else {
        setPhoneError("");
      }
    } else {
      setProfile((prevProfile) => ({ ...prevProfile, [name]: value }));
    }
  };
  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile((prevProfile) => ({
          ...prevProfile,
          profileImage: reader.result as string,
          profileImageFile: file,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleAccountInfo = () => setIsAccountInfoOpen(!isAccountInfoOpen);
  const handleEditProfileDetails = async () => {
    setIsSubmitting(true);
    const controller = new AbortController();
    const signal = controller.signal;

    try {
      const formData = new FormData();

      // Map your state.profileInfo keys to the names you expect in the API
      const fieldMapping: Record<string, string> = {
        firstname: "firstname",
        lastname: "lastname",
        companyName: "companyName",
        username: "preferredName",
        companyBio: "companyBio",
        companyWebsite: "companyWebsite",
        phoneNumber: "phone",
        email: "email",
        companyAddress: "companyAddress",
      };

      Object.keys(fieldMapping).forEach((key) => {
        const apiField = fieldMapping[key];
        const originalValue = state?.profileInfo?.[apiField] ?? "";
        const currentValue = (profile as any)[key] ?? "";

        if (currentValue !== originalValue) {
          formData.append(apiField, currentValue);
        }
      });

      if (profile.profileImage) {
        formData.append("photo", profile.profileImage);
      }

      if ([...formData.keys()].length === 0) {
        toast.info("No changes to update.");
        setIsSubmitting(false);
        return;
      }

      await updateProfile(user.id, formData, signal);
      navigate("/developer-profile", { state: { profileUpdated: true } });
    } catch (err: any) {
      toast(err || err.message || "Error occurred!");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DeveloperDashboardLayout title="Edit profile">
      <div>
        <button
          className=" d-flex flex-row align-items-center gap-1 fs-14 mb-3 btn-el text-link text-dark"
          onClick={() => navigate("/developer-profile")}
        >
          <i className="iconly-Arrow-Left-Circle icli"></i>
          Back To Profile
        </button>
        <div className="flex items-center gap-2">
          <p>Home</p>
          <i className="fa-light fa-angle-right"></i>
          <p className="">Dashboard</p>
          <i className="fa-light fa-angle-right"></i>
          <p className="text-primary">Profile</p>
        </div>
        <div>
          <div className="flex justify-center my-4">
            <ProfileImageUpload
              image={profile.profileImage}
              onImageChange={handleImageChange}
            />
          </div>
          <div>
            <div className="flex justify-between  items-center">
              <div className="flex items-center gap-2 ">
                <i className="fa-regular fa-user text-primary"></i>

                <h6>Personal Information</h6>
              </div>
              <button
                onClick={toggleAccountInfo}
                className="bg-transparent border-0"
              >
                <i
                  className={`fa-regular ${
                    isAccountInfoOpen ? "fa-angle-up" : "fa-angle-down"
                  }`}
                ></i>
              </button>
            </div>
            <hr />
            {isAccountInfoOpen && (
              <div>
                <div className="d-md-flex gap-40">
                  <div className="w-20">
                    <h6>Account Info</h6>
                    <p className="no-text-wrap">
                      This will be displayed on your profile
                    </p>
                  </div>
                  <div className="w-full">
                    <div className="flex gap-20 mb-4">
                      <div className="w-full">
                        <label className="fw-bolder form-label text-capitalize">
                          First name
                        </label>
                        <input
                          type="text"
                          className="form-control rounded-pill py-3 px-4 fs-14"
                          value={profile.firstname}
                          onChange={handleInputChange}
                          name="firstname"
                        />
                      </div>
                      <div className="w-full">
                        <label className="fw-bolder form-label text-capitalize">
                          Last name
                        </label>
                        <input
                          type="text"
                          className="form-control rounded-pill py-3 px-4 fs-14"
                          value={profile.lastname}
                          onChange={handleInputChange}
                          name="lastname"
                        />
                      </div>
                    </div>
                    <div className="w-full my-4">
                      <label className="fw-bolder form-label text-capitalize">
                        Company name
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-pill py-3 px-4 fs-14"
                        value={profile.companyName}
                        name="companyName"
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="w-full my-4">
                      <label className="fw-bolder form-label text-capitalize">
                        Username
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-pill py-3 px-4 fs-14"
                        value={profile.username}
                        name="username"
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="my-4">
                      <label className="fw-bolder form-label text-capitalize">
                        Bio (Company details)
                      </label>
                      <textarea
                        className="form-control form-control-0 ps-3 py-3 border"
                        placeholder=""
                        required
                        rows={5}
                        name="companyBio"
                        value={profile.companyBio}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="w-full my-4">
                      <label className="fw-bolder form-label text-capitalize">
                        Link to company website
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-pill py-3 px-4 fs-14"
                        name="companyWebsite"
                        value={profile.companyWebsite}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="w-full my-4">
                      <label className="fw-bolder form-label text-capitalize">
                        Phone number
                      </label>
                      <input
                        type="text"
                        className={`form-control rounded-pill py-3 px-4 fs-14 ${phoneError ? "is-invalid" : ""}`}
                        name="phoneNumber"
                        value={profile.phoneNumber}
                        onChange={handleInputChange}
                        maxLength={13}
                      />
                      {phoneError && (
                        <div className="invalid-feedback d-block">
                          {phoneError}
                        </div>
                      )}
                    </div>
                    <div className="w-full my-4">
                      <label className="fw-bolder form-label text-capitalize">
                        Email
                      </label>
                      <input
                        type="email"
                        className="form-control rounded-pill py-3 px-4 fs-14"
                        name="email"
                        value={profile.email}
                        onChange={handleInputChange}
                        disabled
                      />
                    </div>
                    <div className="w-full my-4">
                      <label className="fw-bolder form-label text-capitalize">
                        Company Address
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-pill py-3 px-4 fs-14"
                        name="companyAddress"
                        value={profile.companyAddress}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
                <hr />
              </div>
            )}
          </div>

          <div className="w-max ml-auto my-4">
            <button
              className="fw-bold text-white bg-primary rounded-10 p-2 px-3 flex gap-2 items-center"
              onClick={handleEditProfileDetails}
              disabled={isSubmitting || !!phoneError}
            >
              <i className="fa-sharp-duotone fa-solid fa-pen-field"></i>
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                "  Save preferences"
              )}
            </button>
          </div>
        </div>
      </div>
    </DeveloperDashboardLayout>
  );
};

export default EditDeveloperProfile;
